import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  IconBadge,
  Label,
  Textarea,
} from '@platform/ui';
import { MoreHorizontal, TriangleAlert, type LucideIcon } from 'lucide-react';
import { Fragment, type ReactNode, useId, useState } from 'react';

import { errorMessage } from '../lib/api';
import { FormError } from './form';

export interface MenuAction {
  label: string;
  icon?: LucideIcon;
  onSelect: () => void;
  destructive?: boolean;
  /** Dibuja un separador antes (agrupa las acciones peligrosas al final). */
  separatorBefore?: boolean;
}

/**
 * Acciones de una fila: una acción principal visible (la que se usa casi siempre) y el resto en un
 * menú "⋯". Las peligrosas van al final, separadas y en rojo.
 */
export function RowActions({
  primary,
  items,
  label,
}: {
  primary?: ReactNode;
  items: readonly MenuAction[];
  /** Para el nombre accesible del menú: "Más acciones para <label>". */
  label: string;
}) {
  return (
    <div className="flex items-center justify-end gap-1.5">
      {primary}
      {items.length > 0 ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="size-8"
              aria-label={`Más acciones para ${label}`}
            >
              <MoreHorizontal />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-52">
            {items.map((a) => (
              <Fragment key={a.label}>
                {a.separatorBefore ? <DropdownMenuSeparator /> : null}
                <DropdownMenuItem
                  variant={a.destructive ? 'destructive' : 'default'}
                  onSelect={() => {
                    a.onSelect();
                  }}
                >
                  {a.icon ? <a.icon /> : null}
                  {a.label}
                </DropdownMenuItem>
              </Fragment>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null}
    </div>
  );
}

/**
 * Confirmación de una acción con consecuencias (suspender, cancelar, anular…). Reemplaza a
 * `confirm()`/`prompt()`: explica qué pasa, pide el motivo si queda en el historial y muestra el
 * error de la API en el mismo diálogo.
 */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  destructive = true,
  reason,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: ReactNode;
  confirmLabel: string;
  destructive?: boolean;
  /** Si se pasa, pide un motivo obligatorio (queda en el historial). */
  reason?: { label: string; placeholder?: string };
  onConfirm: (reason: string) => Promise<unknown>;
}) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const id = useId();
  const close = (v: boolean) => {
    if (!v) {
      setText('');
      setError(null);
    }
    onOpenChange(v);
  };
  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent>
        <DialogHeader className="flex-row items-start gap-3">
          <IconBadge tone={destructive ? 'destructive' : 'brand'} size="lg">
            <TriangleAlert />
          </IconBadge>
          <div className="flex flex-col gap-1.5">
            <DialogTitle>{title}</DialogTitle>
            <DialogDescription>{description}</DialogDescription>
          </div>
        </DialogHeader>
        <form
          className="flex flex-col gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            setBusy(true);
            setError(null);
            onConfirm(text.trim())
              .then(() => {
                close(false);
              })
              .catch((err: unknown) => {
                setError(errorMessage(err));
              })
              .finally(() => {
                setBusy(false);
              });
          }}
        >
          {reason ? (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={id}>{reason.label}</Label>
              <Textarea
                id={id}
                value={text}
                required
                placeholder={reason.placeholder}
                onChange={(e) => {
                  setText(e.target.value);
                }}
              />
            </div>
          ) : null}
          <FormError message={error} />
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                close(false);
              }}
            >
              Volver
            </Button>
            <Button
              type="submit"
              variant={destructive ? 'destructive' : 'default'}
              disabled={busy || (reason ? text.trim().length === 0 : false)}
            >
              {confirmLabel}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
